import { NavLink } from "react-router-dom";
import { TID } from "@/constants/testIds";
import {
  LayoutGrid, ListTodo, ClipboardCheck, Users, BarChart3, BellRing, ClipboardList, ListPlus, Flame,
  CalendarClock, CalendarDays, Folder, Wallet, Trophy, Video, Package, FileSignature, CalendarRange, Sun, Target, CalendarOff,
} from "lucide-react";

// Single source of truth for navigation — used by the sidebar and global search.
export function buildNavSections(user, { canFinance, canHotLeads }) {
  const sa = !!user?.is_super_admin;
  const adm = !!user?.is_admin;
  const isManager = user?.role === "manager" || sa;
  const shoot = sa || adm || user?.team === "Shoot Team";
  const sections = [
    { title: "Work", items: [
      { to: "/my-day", icon: Sun, label: "My Day", testid: "sidebar-nav-my-day" },
      { to: "/", icon: LayoutGrid, label: "Whiteboard", testid: TID.sidebarWhiteboard },
      { to: "/my-tasks", icon: ListTodo, label: "My Tasks", testid: TID.sidebarMyTasks },
      !sa && { to: "/daily-log", icon: ClipboardCheck, label: "Daily Log", testid: TID.sidebarDailyLog },
      { to: "/monthly-targets", icon: Target, label: "Monthly Targets", testid: "sidebar-nav-monthly-targets" },
      { to: "/projects", icon: Folder, label: "Projects", testid: "sidebar-nav-projects" },
      { to: "/leaves", icon: CalendarOff, label: "Leaves", testid: "sidebar-nav-leaves" },
      { to: "/alarms", icon: BellRing, label: "Alarms", testid: "sidebar-nav-alarms" },
      (sa || adm || user?.team === "Digital Team") && { to: "/social-calendar", icon: CalendarClock, label: "Social Calendar", testid: "sidebar-nav-social-calendar" },
    ] },
    { title: "Reports", items: [
      { to: "/reports", icon: BarChart3, label: "Reports & KRA", testid: TID.sidebarReports },
      { to: "/weekly-reports", icon: Trophy, label: "Weekly & Leaderboard", testid: "sidebar-nav-weekly" },
      (isManager || adm) && { to: "/monthly-report", icon: CalendarRange, label: "Monthly Report", testid: "sidebar-nav-monthly-report" },
      isManager && { to: "/team-logs", icon: ClipboardList, label: "Team Logs", testid: "sidebar-nav-team-logs" },
    ] },
    shoot && { title: "Shoots", items: [
      { to: "/bookings-calendar", icon: CalendarDays, label: "Bookings Calendar", testid: "sidebar-nav-bookings-calendar" },
      { to: "/shoots", icon: Video, label: "Shoots Calendar", testid: "sidebar-nav-shoots" },
      { to: "/shoots/new", icon: ClipboardList, label: "Pre-shoot Checklist", testid: "sidebar-nav-shoots-new" },
      { to: "/shoots/equipment", icon: Package, label: "Equipment", testid: "sidebar-nav-equipment" },
      { to: "/shoots/booking-slip", icon: FileSignature, label: "Studio Booking Slip", testid: "sidebar-nav-booking-slip" },
    ] },
    { title: "Finance", items: [
      canFinance && { to: "/finance", icon: Wallet, label: "Finance", testid: "sidebar-nav-finance" },
      canHotLeads && { to: "/hot-leads", icon: Flame, label: "Hot Leads", testid: "sidebar-nav-hot-leads" },
    ] },
    isManager && { title: "Manage", items: [
      sa && { to: "/team", icon: Users, label: "Team", testid: TID.sidebarTeam },
      { to: "/backlog", icon: ListPlus, label: "Backlog", testid: "sidebar-nav-backlog" },
    ] },
  ];
  return sections
    .filter(Boolean)
    .map((s) => ({ ...s, items: s.items.filter(Boolean) }))
    .filter((s) => s.items.length);
}

function Item({ to, icon: Icon, label, testid, onClick, collapsed }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testid}
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `group flex items-center gap-3 h-9 rounded-md text-sm transition-colors ${collapsed ? "justify-center px-0" : "px-3"} ${
          isActive
            ? "bg-[#E11D2E]/10 text-[#E11D2E] font-semibold"
            : "text-white/65 hover:text-white hover:bg-white/5"
        }`
      }
    >
      <Icon size={17} className="shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );
}

export const SidebarNav = ({ sections, collapsed, onNavigate }) => (
  <nav className="px-2.5 py-3 space-y-4 flex-1 overflow-y-auto scrollbar-thin" data-testid="sidebar-nav">
    {sections.map((s) => (
      <div key={s.title} data-testid={`sidebar-section-${s.title.toLowerCase()}`}>
        {collapsed
          ? <div className="mx-3 mb-2 border-t border-white/10" />
          : <div className="overline px-3 pb-1.5">{s.title}</div>}
        <div className="space-y-0.5">
          {s.items.map((it) => <Item key={it.to} {...it} collapsed={collapsed} onClick={onNavigate} />)}
        </div>
      </div>
    ))}
  </nav>
);
